import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { Droppable, FONT_WEIGHT_BOLD, Icon, showDndDebugInfo } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { CONDITION_TYPE, GROUP_CONDITION_NAME, TYPE_DROPPABLE } from "../../constants";
import type { DroppableData } from "../../dnd/dndSchemas";
import { ChildConditionsEditor } from "../../editors/ChildConditionsEditor";
import { ConditionPlaceholderDropzone, ResponseZoneContainer, ResponseZoneHeader } from "../../editors/shared";
import { makeConditionDroppableId } from "../../editors/utils";
import { useResponseEditorContext } from "../../response-editor";
import { useIsRootCondition, useResponseDesigner } from "../../ResponseByIdContext";
import type { groupConditionBuilder } from "./groupConditionBuilder";
import { groupOperators } from "./types";

const GroupOperatorPicker = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme.secondary};
  font-weight: ${FONT_WEIGHT_BOLD};
  margin-right: 4px;
`;

export const GroupConditionEditor: typeof groupConditionBuilder.views.edit = ({
  id,
  config: { operator },
  onConfigChange,
  children: { values },
}) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { removeCondition } = useResponseDesigner();
  const { disabledDropzoneIds } = useResponseEditorContext();
  const isRootCondition = useIsRootCondition(id);

  const handleOperatorChange = () => {
    const index = groupOperators.indexOf(operator);
    const nextIndex = (index + 1) % groupOperators.length;

    onConfigChange({ operator: groupOperators[nextIndex] });
  };

  const isDropDisabled = disabledDropzoneIds.includes(id);

  return (
    <>
      {showDndDebugInfo && (
        <span
          css={css`
            position: absolute;
            background: rgba(0, 0, 0, 0.25);
            color: #fff;
          `}
        >
          Group: {id}
        </span>
      )}
      <ResponseZoneContainer>
        <ResponseZoneHeader>
          <GroupOperatorPicker onClick={handleOperatorChange} data-test="group-operator-button">
            {formatMessage(operator)} <Icon icon="angle-down" size={12} />
          </GroupOperatorPicker>
          {formatMessage("conditionsMet")}
        </ResponseZoneHeader>

        <Droppable
          id={makeConditionDroppableId(id)}
          data={
            {
              rule: { id, type: TYPE_DROPPABLE, ruleType: CONDITION_TYPE, typeId: GROUP_CONDITION_NAME },
            } satisfies DroppableData
          }
          hideBorder={!values.length}
          disabled={isDropDisabled}
          accepts={{ "rule.ruleType": [CONDITION_TYPE] }}
          color="secondary"
        >
          {values.length ? (
            <ChildConditionsEditor
              conditions={values}
              parentGroupId={id}
              onRemoveCondition={removeCondition}
              isRootCondition={isRootCondition}
            />
          ) : (
            <ConditionPlaceholderDropzone>{formatMessage("noConditionsMessage")}</ConditionPlaceholderDropzone>
          )}
        </Droppable>
      </ResponseZoneContainer>
    </>
  );
};

const formatStrings = {
  any: {
    en: "When ANY",
  },
  all: {
    en: "When ALL",
  },
  none: {
    en: "When NONE",
  },
  conditionsMet: {
    en: "of the following conditions are met:",
  },
  noConditionsMessage: {
    en: "Drag conditions here.",
  },
};
